<template>
  <div v-show="inCall">
    <div class="flex">
      <div class="items-center bg-50 text-black rounded q-py-xs q-px-sm"
           :class="{'top-right': inCall || ringing }">
        <span class="q-px-xs">{{ status }}</span>

        <span v-if="ringing || inCall" class="ml-1 font-bold">{{ customerName }}</span>

        <v-button v-if="route"
                  type="button"
                  @click.prevent="openRoute"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-primary">
          {{ $t('View Customer') }}
        </v-button>

        <v-button v-if="inCall && !readRecordingScript"
                  type="button"
                  @click.prevent="allowRecording"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-danger">
          {{ $t('Record') }}
        </v-button>

        <v-button v-if="inCall"
                  type="button"
                  @click.prevent="hangUp"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-danger">
          {{ $t('Hang up') }}
        </v-button>

        <v-button v-if="ringing"
                  type="button"
                  @click.prevent="answer"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-primary">
          {{ $t('Answer') }}
        </v-button>

        <v-button v-if="ringing"
                  type="button"
                  @click.prevent="reject"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-danger">
          {{ $t('Reject') }}
        </v-button>

        <v-button v-if="!ready"
                  type="button"
                  :disabled="working"
                  @click.prevent="setup"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-primary">
          {{ $t('Turn on') }}
        </v-button>

        <v-button v-else
                  type="button"
                  :disabled="working"
                  @click.prevent="destroy"
                  class="btn text-80 font-normal ml-2 h-9 px-3 btn-danger">
          {{ $t('Turn off') }}
        </v-button>
      </div>
      <ring-tone :ringing="ringing"/>
    </div>
  </div>
</template>

<style>
.top-right {
  top: 8px;
  right: 8px
}
</style>

<script>
import {Device} from 'twilio-client'
import RingTone from './RingTone'
import axios from "axios";


export default {
  components: {RingTone},

  data () {
    return {
      incomingConnection: null,
      ringing: false,
      inCall: false,
      ready: false,
      working: false,
      customerPhone: null,
      customerName: null,
      route: null,
      readRecordingScript: false,
      showRecordingScript: false,
      status: this.$t('Phone offline'),
    }
  },

  mounted () {
    this.registerDeviceEvents()
    this.$root.$on('soft-phone-call', this.callCustomer)
    this.$root.$on('soft-phone-hang-up', this.hangUp)
    this.$root.$on('soft-phone-ping', this.pong)
  },

  beforeDestroy () {
    this.$root.$off('soft-phone-call', this.callCustomer)
    this.$root.$off('soft-phone-hang-up', this.hangUp)
    this.$root.$off('soft-phone-ping', this.pong)
  },

  methods: {
    recordPreFlight () {
      this.showRecordingScript = true
    },

    clear () {
      this.customerPhone = this.customerName = this.route = null
      this.readRecordingScript = false
    },

    openRoute () {
      this.$router.push(this.route)
    },

    async setup () {
      this.working = true
      await axios.post('/api/v1/twilio', {
        endpoint: '/nova-vendor/external-twilio/token',
        method: 'get'
      })
          .then(({data}) => this.setupDevice(data.token))
    },

    destroy () {
      Device.destroy()
      this.ready = false
    },

    /* Helper function to update the call status bar */
    updateCallStatus (status) {
      this.status = status
    },

    allowRecording () {
      console.log(this.incomingConnection)
      this.readRecordingScript = true
      this.showRecordingScript = false
      if (!this.incomingConnection) {
        return
      }
      const sid = this.incomingConnection.parameters.CallSid
      axios.post('/api/v1/twilio', {
        endpoint: '/nova-vendor/external-twilio/allow-recording/' + sid,
        method: 'get'
      }).then(() => this.show(this.$t('The call will be recorded!'), {type: 'success'}))
          .catch(() => this.show(this.$t('This call may not be recorded. Something went wrong!'), {type: 'error'}))
    },

    updateCustomer (phone) {
      this.customerPhone = phone
      axios.post('/api/v1/twilio', {
        endpoint: '/nova-vendor/external-twilio/identify/' + phone,
        method: 'get'
      }).then(({data}) => this.processCustomer(data))
    },

    processCustomer (data) {
      this.customerName = data.name || this.customerPhone
      this.callableType = data.callableType
      this.callableId = data.callableId
    },

    /* Call a customer from a support ticket */
    async callCustomer (options) {
      await this.setup()
      /* if (!this.ready) {
        this.show(this.$t('Your phone is off!'), {type: 'error'})
        return
      } */

      if (this.inCall) {
        this.show(this.$t('Already in call!'), {type: 'error'})
        return
      }

      this.updateCustomer(options.phoneNumber);
      this.updateCallStatus(this.$t('Calling'))
      this.$nextTick(() => {
        Device.connect(options)
      })

    },

    /* Emit a global pong. Usefull to check if  */
    pong () {
      if (this.inCall) {
        this.$root.$emit('soft-phone-in-call', this.customerPhone)
      } else {
        this.$root.$emit('soft-phone-free')
      }
    },

    /* End a call */
    hangUp () {
      Device.disconnectAll()
      this.$emit('soft-phone-hung-up')
      this.destroy()
    },

    /* Setup Twilio Device */
    setupDevice (token) {
      Device.setup(token, {})
    },

    registerDeviceEvents () {
      //https://www.twilio.com/docs/voice/client/javascript/device#events
      Device
          /* This is triggered when an incoming connection is canceled by the caller before it is accepted by the
           Twilio Client device. */
          .on('cancel', this.onCancel)

          /* This is triggered when a connection is opened, whether initiated using .connect() or via an accepted
           incoming connection. */
          .on('connect', this.onConnection)

          /* Fired any time a Connection is closed. The handler function receives the Twilio.Connection object that
           was just closed as an argument. */
          .on('disconnect', this.onDisconnect)

          /* Emitted when any device error occurs. These may be errors in your request, your token, connection errors,
           or other application errors*/
          .on('error', this.onError)

          /* This is triggered when the connection to Twilio drops or the device's token is invalid/expired. */
          .on('offline', this.onOffline)

          /* This is triggered whenever an incoming connection from an outbound REST call or a TwiML <Dial> to this
           device is made. */
          .on('incoming', this.onIncoming)

          /* This is initially triggered when all operations in .setup() have completed and the device is ready and
           online. It will be triggered again if the device goes offline and comes back online (i.e. the connection
           drops and returns). */
          .on('ready', this.onReady)
    },

    answer () {
      this.ringing = false
      this.incomingConnection.accept()
    },

    reject () {
      this.incomingConnection.reject()
      this.onDisconnect(this.incomingConnection)
    },

    onReady (device) {
      this.ready = true
      this.working = false

      this.updateCallStatus(this.$t('Phone ready'))
    },

    onConnection (connection) {
      this.inCall = true
      console.log(this.inCall)

      if (connection.parameters.From) {
        this.updateCustomer(connection.parameters.From);
      }

      this.$emit('soft-phone-in-call', this.customerPhone)

      this.updateCallStatus(this.$t('In call:'))
    },

    onIncoming (connection) {
      this.ringing = true
      this.updateCustomer(connection.parameters.From);

      this.updateCallStatus(this.$t('Incoming call:'))

      this.incomingConnection = connection
    },

    onCancel (connection) {
      this.onDisconnect(connection)
    },

    onDisconnect (connection) {
      this.inCall = this.ringing = false
      this.clear()
      this.updateCallStatus(this.$t('Phone ready'))
    },

    onError (error) {
      this.inCall = this.ringing = false
      this.updateCallStatus(this.$t('ERROR: :message', {message: error.message}))
    },

    onOffline (device) {
      this.inCall = this.ringing = this.working = this.ready = false
      this.updateCallStatus(this.$t('Phone offline'))
    },
    show (message, options) {
      this.$q.notify({
        color: options.type === 'success' ? 'positive' : 'negative',
        icon: options.type === 'success' ? 'thumb_up' : 'thumb_down',
        message: message
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/SoftPhone.vue